<template>
  <div
    ref="containerRef"
    :class="[
      'ScrollSnap',
      {
        'is-mobile': isMobile,
        'x-mandatory': props.scrollSnapType === ScrollSnapType.X_MANDATORY,
        'y-mandatory': props.scrollSnapType === ScrollSnapType.Y_MANDATORY,
        'x-scroll': props.scrollAxis === ScrollAxis.X_SCROLL,
        'y-scroll': props.scrollAxis === ScrollAxis.Y_SCROLL,
        'x-hidden': props.scrollAxis === ScrollAxis.X_HIDDEN,
        'y-hidden': props.scrollAxis === ScrollAxis.Y_HIDDEN,
      },
    ]"
    @scroll="onScroll"
    @scrollend="onScrollEnd"
    v-bind="$attrs"
  >
    <slot :entered-index="enteredIndex" :snapped-index="snappedIndex" />
  </div>
</template>

<script lang="ts">
export enum ScrollSnapType {
  Y_MANDATORY = 'y_mandatory',
  X_MANDATORY = 'x_mandatory',
}

export enum ScrollAxis {
  X_SCROLL = 'x_scroll',
  Y_SCROLL = 'y_scroll',
  X_HIDDEN = 'x_hidden',
  Y_HIDDEN = 'y_hidden',
}

export interface ScrollSnapProps {
  scrollSnapType: ScrollSnapType;
  scrollAxis: ScrollAxis;
  slideCount: number;
}
</script>

<script lang="ts" setup>
import { isMobile } from '@/core/helpers/ui.helper';
import { onBeforeUnmount, onMounted, ref } from 'vue';

let resizeObserver: ResizeObserver | null = null;
let scrollEndFallbackTimer: ReturnType<typeof setTimeout> | null = null;
const supportsScrollEnd = 'onscrollend' in window;
let prevOffset = 0;
let clientWidth = 0;
let clientHeight = 0;
let isCorrectingPosition = false;
let hasScrolledSinceLastEnd = false;

const props = defineProps<ScrollSnapProps>();
const isVertical = props.scrollAxis === ScrollAxis.Y_SCROLL || props.scrollAxis === ScrollAxis.Y_HIDDEN;

const emit = defineEmits<{
  onEntered: [index: number];
  onSnapped: [index: number];
}>();

const containerRef = ref<HTMLElement | null>(null);
const enteredIndex = ref(0);
const snappedIndex = ref(0);

function updateClientDimensions(el: HTMLElement) {
  clientWidth = el.clientWidth;
  clientHeight = el.clientHeight;
}

function resolveEntered(progress: number, isScrollForward: boolean): number | null {
  if (isScrollForward) {
    return Math.ceil(progress);
  } else {
    return Math.floor(progress);
  }
}

function resolveSnapped(progress: number, isScrollForward: boolean): number | null {
  if (isScrollForward) {
    return Math.floor(progress);
  } else {
    return Math.ceil(progress);
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (!containerRef.value) return;

  const tag = (event.target as HTMLElement)?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

  const key = event.key;
  const size = isVertical ? clientHeight : clientWidth;
  const delta = isVertical
    ? key === 'ArrowDown'
      ? 1
      : key === 'ArrowUp'
        ? -1
        : 0
    : key === 'ArrowRight'
      ? 1
      : key === 'ArrowLeft'
        ? -1
        : 0;
  if (!delta || size === 0) return;

  event.preventDefault();
  const nextIndex = Math.max(0, Math.min(snappedIndex.value + delta, props.slideCount - 1));
  if (nextIndex === snappedIndex.value) return;

  containerRef.value.scrollTo({
    [isVertical ? 'top' : 'left']: nextIndex * size,
    behavior: 'smooth',
  });
}

function onScrollEnd() {
  const el = containerRef.value;
  if (!el) return;
  if (!hasScrolledSinceLastEnd) return;

  hasScrolledSinceLastEnd = false;

  const size = isVertical ? clientHeight : clientWidth;
  if (size === 0) return;

  const offset = isVertical ? el.scrollTop : el.scrollLeft;
  const progress = offset / size;
  const rounded = Math.round(progress);
  const drift = Math.abs(progress - rounded);

  // Below ~1% the browser has already settled a mandatory snap; correcting a
  // sub-pixel error here only produces a visible nudge.
  if (drift > 0.01 && drift < 0.05) {
    isCorrectingPosition = true;
    el.scrollTo({ [isVertical ? 'top' : 'left']: rounded * size, behavior: 'instant' });
    requestAnimationFrame(() => {
      isCorrectingPosition = false;
    });
  }

  const settledIndex = rounded;
  if (settledIndex !== snappedIndex.value) {
    snappedIndex.value = settledIndex;
    emit('onSnapped', settledIndex);
  }
}

function onScroll(event: Event) {
  const el = event.currentTarget as HTMLElement | null; // ?? containerRef.value;
  if (!el) return;

  if (isCorrectingPosition) return;

  const offset = isVertical ? el.scrollTop : el.scrollLeft;
  if (offset !== prevOffset) {
    hasScrolledSinceLastEnd = true;
  }

  if (
    (isVertical && props.scrollAxis === ScrollAxis.Y_HIDDEN) ||
    (!isVertical && props.scrollAxis === ScrollAxis.X_HIDDEN)
  ) {
    // Placates scroll inertia
    event.preventDefault();
  }

  const size = isVertical ? clientHeight : clientWidth;
  const isScrollForward = prevOffset < offset;
  const progress = offset / size;

  prevOffset = offset;

  const nextEnteredIndex = resolveEntered(progress, isScrollForward);
  if (nextEnteredIndex !== null && nextEnteredIndex !== enteredIndex.value) {
    enteredIndex.value = nextEnteredIndex;
    emit('onEntered', nextEnteredIndex);
  }

  const nextSnappedIndex = resolveSnapped(progress, isScrollForward) || 0;

  if (nextSnappedIndex >= props.slideCount - 1 || nextSnappedIndex <= 0) {
    event.preventDefault();
  }

  if (!supportsScrollEnd) {
    if (scrollEndFallbackTimer) clearTimeout(scrollEndFallbackTimer);
    scrollEndFallbackTimer = setTimeout(() => onScrollEnd(), 120);
  }
}

onMounted(() => {
  const el = containerRef.value;
  if (!el) return;

  updateClientDimensions(el);

  if (typeof ResizeObserver === 'undefined') return;

  resizeObserver = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (!entry) return;
    if (!(entry.target instanceof HTMLElement)) return;

    updateClientDimensions(entry.target);
  });

  resizeObserver.observe(el);

  window.addEventListener('keydown', onKeyDown);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  if (scrollEndFallbackTimer) clearTimeout(scrollEndFallbackTimer);
  window.removeEventListener('keydown', onKeyDown);
});
</script>

<style lang="scss" scoped>
.ScrollSnap {
  width: 100%;
  height: 100%;
}

.ScrollSnap::-webkit-scrollbar {
  display: none;
}

.x-mandatory {
  scroll-snap-type: x mandatory;
}

.y-mandatory {
  scroll-snap-type: y mandatory;
}

.x-scroll {
  overflow-x: scroll;
}

/* `overflow-x: visible` computes to `auto` next to a scrolling axis, which turns
   sub-pixel card widths into a second snap axis and shifts slides sideways. */
.y-scroll {
  overflow-y: scroll;
  overflow-x: clip;
}

.x-hidden {
  overflow-x: hidden;
}

.y-hidden {
  overflow-y: hidden;
  overflow-x: clip;
}
</style>
